import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { doGetClaims, doGetClaim, doUpdateClaim, doGetActiveClaimsCount } from './thunk';
import { RootState } from '@/redux/store';
import { ClaimType } from '../types';

const isActionWithPending = (action) => {
  return action.type && action.type.startsWith('claims/') && action.type.endsWith('/pending');
};

const isActionWithFulfilled = (action) => {
  return action.type && action.type.startsWith('claims/') && action.type.endsWith('/fulfilled');
};

const isActionWithFailed = (action) => {
  return action.type && action.type.startsWith('claims/') && action.type.endsWith('/rejected');
};

const claimsAdapter = createEntityAdapter<ClaimType>({
  selectId: (item: any) => item.id,
});

const initialState = {
  action: null,
  isFetching: false,
  claim: undefined,
  activeClaimCount: null,
  status: 'idle',
  metadata: {},
  claims: claimsAdapter.getInitialState(),
};

const claimSlice = createSlice({
  name: 'claim',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(doGetClaims.pending, (state) => {
      state.action = 'getall';
      state.status = 'loading';
    });
    builder.addCase(doGetClaims.fulfilled, (state, action) => {
      const { claims = [], pagination = {} } = action.payload || {};
      state.status = 'succeeded';
      state.metadata = pagination;
      claimsAdapter.setAll(state.claims, claims);
    });
    builder.addCase(doGetClaims.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doGetClaim.pending, (state) => {
      state.action = 'getone';
      state.status = 'loading';
    });
    builder.addCase(doGetClaim.fulfilled, (state, action) => {
      const claim = action.payload;
      state.status = 'succeeded';
      claimsAdapter.upsertOne(state.claims, claim);
    });
    builder.addCase(doGetClaim.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(doUpdateClaim.pending, (state) => {
      state.action = 'update';
      state.status = 'loading';
    });
    builder.addCase(doGetActiveClaimsCount.pending, (state) => {
      state.action = 'getcount';
      state.status = 'loading';
    });
    builder.addCase(doGetActiveClaimsCount.fulfilled, (state, action) => {
      const { claims } = action.payload;
      state.activeClaimCount = claims;
      state.status = 'succeeded';
    });
    builder.addCase(doGetActiveClaimsCount.rejected, (state) => {
      state.action = 'getcount';
      state.status = 'failed';
    });
    builder.addCase(doUpdateClaim.fulfilled, (state, action) => {
      const claim = action.payload;
      const existingClaim = state.claims.ids.find((id) => id === claim.id);
      if (existingClaim) {
        claimsAdapter.updateOne(state.claims, { id: claim.id, changes: claim });
      }
      state.status = 'succeeded';
    });
    builder.addCase(doUpdateClaim.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addMatcher(isActionWithPending, (state) => {
      state.isFetching = true;
    });
    builder.addMatcher(isActionWithFulfilled, (state) => {
      state.isFetching = false;
    });
    builder.addMatcher(isActionWithFailed, (state) => {
      state.isFetching = false;
    });
  },
});

export const {
  selectAll: selectAllClaims,
  selectById: selectClaimById,
  selectIds: selectClaimIds,
} = claimsAdapter.getSelectors((state: RootState) => state.claim.claims);

export const selectActiveClaimCount = (state) => state.claim.activeClaimCount;

const { reducer: claimReducer } = claimSlice;
export default claimReducer;
